import { accountIconHtml, escapeHtml } from '@colplat/ui-web';
import { appHref } from '../globals.js';
import type { AccountDto } from './webledgerOfflineState.js';

/**
 * How an account is shown wherever one is named — its icon, and its name with
 * the path in front of it.
 *
 * Shared because legacy puts one in every one of those places — the tree, a
 * register's Transfer cell, a split line, a report card's account column —
 * and an icon that appears in some of them and not others is worse than none
 * at all: the reader learns to scan for a shape, and then the shape is
 * missing exactly where two accounts have similar names.
 *
 * Two layers, because the icon set is inherited. Roughly half the accounts
 * carry a `iconPath` seeded from legacy; the rest fall back to the glyph for
 * their type, which is not a placeholder but a real answer — "this is an
 * expense account" is most of what the icon was telling you anyway.
 */

/** Resolve an `iconPath` to a URL: a seeded filename, or an uploaded record. */
export function iconUrl(iconPath: string): string {
  return iconPath.startsWith('u/')
    ? appHref(`/apps/webledger/icons/${iconPath}`)
    : appHref(`/icons/${iconPath}`);
}

type IconLike = Pick<AccountDto, 'type' | 'isPlaceholder'> & { iconPath?: string };

/**
 * The seeded icon if the account has one, else the generic per-type glyph.
 *
 * For a list of accounts — the chart, the account editor. `onerror` rather
 * than checking first: the icon set is seeded from legacy data and two
 * accounts reference uploads that are somebody's files, not the app's, so a
 * missing one is expected rather than exceptional. Carrying the replacement
 * glyph in an attribute costs about 400 bytes per row, which is the right
 * price for 59 rows and the wrong one for 500 — see `inlineAccountIcon`.
 *
 * It **warns as well as falling back**. A silent substitution is what hid the
 * icon route answering 401 to every `<img>` for as long as it did: a missing
 * icon and a forbidden one degrade to the same glyph, so the page looked
 * correct either way and nobody had a reason to look. One line in the console
 * is the difference between a bug that is found and one that waits for the
 * first person to upload an icon.
 */
export function accountIcon(account: IconLike): string {
  if (!account.iconPath) return accountIconHtml(account.type, account.isPlaceholder);
  return `<img class="wl-acct-icon" src="${escapeHtml(iconUrl(account.iconPath))}" alt="" `
    + `onerror="console.warn('[webledger] icon did not load:', this.src);`
    + `this.replaceWith(document.createRange().createContextualFragment(this.dataset.fallback))" `
    + `data-fallback="${escapeHtml(accountIconHtml(account.type, account.isPlaceholder))}">`;
}

/**
 * The same icon, inline before a name, for a place that has an account *id*
 * rather than the account — a split line, a Transfer cell, a trial-balance
 * row.
 *
 * Wrapped rather than styled in place: the chart of accounts lays its icon
 * out with flexbox and a gap, and these sit in running text inside a table
 * cell. One wrapper keeps the two from having to agree about spacing.
 *
 * Nothing at all when the account is not in the list, which is a real state
 * rather than an error: a register opened offline has whatever accounts were
 * cached, and a leg can name one created since. A missing glyph there is the
 * right amount of wrong — better than a gap where an icon is supposed to be.
 *
 * A broken icon file is dropped rather than swapped for the type glyph, and
 * that is the one place this differs from `accountIcon`. A report card list
 * carries 500-odd of these, and shipping the replacement glyph in an
 * attribute beside every one of them was measured at 200KB of markup — an
 * eighth of the page — to cover the two accounts whose uploads are missing.
 * On a split line an absent icon costs the reader nothing; 200KB does.
 */
export function inlineAccountIcon(accounts: readonly AccountDto[], id: string): string {
  const account = accounts.find((a) => a.id === id);
  if (!account) return '';
  const inner = account.iconPath
    ? `<img class="wl-acct-icon" src="${escapeHtml(iconUrl(account.iconPath))}" alt="" onerror="this.remove()">`
    : accountIconHtml(account.type, account.isPlaceholder);
  return `<span class="wl-ico">${inner}</span>`;
}

/**
 * An account label with its leading path dimmed — legacy's `acctLabelHtml`.
 *
 * `Assets ➤ Current ➤ Bank` is one piece of information with two jobs in it.
 * The last segment is the account; everything before it is context, there to
 * disambiguate two accounts called "Cash". Printed at one weight the reader
 * has to parse the whole string every time to find the part that matters,
 * and a column of them reads as a column of prefixes.
 *
 * Opacity rather than a colour, so it works inside a link and against any
 * background — including a marked row's amber and an out-of-scope leg's
 * already-muted ink, both of which a hardcoded grey would fight.
 */
export function accountLabelHtml(label: string, separator = ' ➤ '): string {
  const parts = String(label ?? '').split(separator);
  const leaf = escapeHtml(parts.pop() ?? '');
  if (parts.length === 0) return leaf;
  return `<span class="wl-acct-path">${escapeHtml(parts.join(separator) + separator)}</span>${leaf}`;
}
